import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API_ORIGIN } from '../../lib/apiBase';
import { Segmented } from './dash/Toolbar';
import { CanvasSection, CanvasTitle, EditorShell, EditorStatus, useDirty } from './editor/EditorShell';
import { AutoTextarea, Field, ImageField, InspectorSection, TagInput, TextInput } from './editor/fields';
import { ClientStrip } from './casestudy/ClientStrip';
import { StatsSection } from './casestudy/StatsSection';
import { ProcessStepsSection } from './casestudy/ProcessStepsSection';
import { ResultsSection } from './casestudy/ResultsSection';
import { GallerySection } from './casestudy/GallerySection';
import { CaseStudyStat, ProcessStep, CaseStudyResult, GalleryItem, generateId } from './casestudy/types';

const API_BASE = process.env.NODE_ENV === 'production' ? `${API_ORIGIN}/api/blog` : `${API_ORIGIN}/.netlify/functions/blog`;

interface CaseStudyData {
  id?: string;
  slug: string;
  title: string;
  subtitle: string;
  clientName: string;
  clientLocation: string;
  industry: string;
  tags: string[];
  featuredImage?: string;
  iconName?: string;
  status: EditorStatus;
  publishedAt?: string;
  challenge: string;
  solution: string;
  deliverables: string[];
  testimonialText?: string;
  testimonialAuthor?: string;
  testimonialRole?: string;
  stats: CaseStudyStat[];
  processSteps: ProcessStep[];
  results: CaseStudyResult[];
  gallery: GalleryItem[];
}

const EMPTY: CaseStudyData = {
  slug: '',
  title: '',
  subtitle: '',
  clientName: '',
  clientLocation: '',
  industry: '',
  tags: [],
  featuredImage: '',
  iconName: '',
  status: 'draft',
  challenge: '',
  solution: '',
  deliverables: [],
  testimonialText: '',
  testimonialAuthor: '',
  testimonialRole: '',
  stats: [],
  processSteps: [],
  results: [],
  gallery: [],
};

const slugify = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

interface Props {
  caseStudyId?: string;
  /** Leave the editor (back to the list). */
  onCancel?: () => void;
  /** Called after every successful save. */
  onSave?: () => void;
  /** Called once when a new case study is first saved, with its id. */
  onCreated?: (id: string) => void;
}

const CaseStudyEditor: React.FC<Props> = ({ caseStudyId, onCancel, onSave, onCreated }) => {
  const navigate = useNavigate();
  const [doc, setDoc] = useState<CaseStudyData>(EMPTY);
  const [saved, setSaved] = useState<string | null>(caseStudyId ? null : JSON.stringify(EMPTY));
  const [slugTouched, setSlugTouched] = useState(!!caseStudyId);
  const [loading, setLoading] = useState(!!caseStudyId);
  const [saving, setSaving] = useState(false);
  const dirty = useDirty(doc, saved);

  const token = () => localStorage.getItem('admin_token');
  const leave = onCancel || (() => navigate('/admin/blog'));

  useEffect(() => {
    if (!caseStudyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/admin/case-studies`, { headers: { Authorization: `Bearer ${token()}` } });
        if (res.status === 401) return navigate('/admin/login', { replace: true });
        const data = await res.json();
        const cs = data.success ? data.caseStudies.find((c: { id: string }) => c.id === caseStudyId) : null;
        if (!cs) throw new Error('Case study not found');
        const loaded: CaseStudyData = {
          id: cs.id,
          slug: cs.slug,
          title: cs.title,
          subtitle: cs.subtitle || '',
          clientName: cs.clientName || '',
          clientLocation: cs.clientLocation || '',
          industry: cs.industry || '',
          tags: cs.tags || [],
          featuredImage: cs.featuredImage || '',
          iconName: cs.iconName || '',
          status: cs.status === 'published' ? 'published' : 'draft',
          publishedAt: cs.publishedAt,
          challenge: cs.challenge || '',
          solution: cs.solution || '',
          deliverables: cs.deliverables || [],
          testimonialText: cs.testimonialText || '',
          testimonialAuthor: cs.testimonialAuthor || '',
          testimonialRole: cs.testimonialRole || '',
          stats: (cs.stats || []).map((s: CaseStudyStat, i: number) => ({
            id: s.id || generateId(),
            value: s.value,
            label: s.label,
            sortOrder: s.sortOrder ?? i,
          })),
          processSteps: (cs.processSteps || []).map((ps: ProcessStep, i: number) => ({
            id: ps.id || generateId(),
            title: ps.title,
            description: ps.description,
            sortOrder: ps.sortOrder ?? i,
          })),
          results: (cs.results || []).map((r: CaseStudyResult, i: number) => ({
            id: r.id || generateId(),
            title: r.title,
            description: r.description,
            iconName: r.iconName || '',
            sortOrder: r.sortOrder ?? i,
          })),
          gallery: (cs.gallery || []).map((g: GalleryItem, i: number) => ({
            id: g.id || generateId(),
            iconName: g.iconName || '',
            label: g.label || '',
            imageUrl: g.imageUrl || '',
            isLarge: g.isLarge || false,
            sortOrder: g.sortOrder ?? i,
          })),
        };
        if (!cancelled) {
          setDoc(loaded);
          setSaved(JSON.stringify(loaded));
        }
      } catch (err: any) {
        toast.error(err?.message || 'Could not load the case study');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseStudyId, navigate]);

  const set = <K extends keyof CaseStudyData>(key: K, value: CaseStudyData[K]) => setDoc((d) => ({ ...d, [key]: value }));

  const save = useCallback(
    async (publish: boolean) => {
      if (!doc.title.trim()) {
        toast.error('Give the case study a title first');
        return;
      }
      if (!doc.clientName.trim()) {
        toast.error('Add the client name first');
        return;
      }
      setSaving(true);
      try {
        const status: EditorStatus = publish ? 'published' : doc.status;
        const next: CaseStudyData = {
          ...doc,
          slug: doc.slug || slugify(doc.title),
          status,
          publishedAt: status === 'published' && !doc.publishedAt ? new Date().toISOString() : doc.publishedAt,
        };
        const payload = {
          ...next,
          stats: next.stats.map((s, i) => ({ value: s.value, label: s.label, sortOrder: i })),
          processSteps: next.processSteps.map((ps, i) => ({ title: ps.title, description: ps.description, sortOrder: i })),
          results: next.results.map((r, i) => ({ title: r.title, description: r.description, iconName: r.iconName, sortOrder: i })),
          gallery: next.gallery.map((g, i) => ({ iconName: g.iconName, label: g.label, imageUrl: g.imageUrl, isLarge: g.isLarge, sortOrder: i })),
        };
        const res = await fetch(caseStudyId ? `${API_BASE}/admin/case-studies/${caseStudyId}` : `${API_BASE}/admin/case-studies`, {
          method: caseStudyId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify(payload),
        });
        if (res.status === 401) return navigate('/admin/login', { replace: true });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Could not save the case study');
        setDoc(next);
        setSaved(JSON.stringify(next));
        toast.success(publish && doc.status !== 'published' ? 'Case study published' : 'Case study saved');
        onSave?.();
        if (!caseStudyId && data.caseStudy?.id) onCreated?.(data.caseStudy.id);
      } catch (err: any) {
        toast.error(err?.message || 'Could not save the case study');
      } finally {
        setSaving(false);
      }
    },
    [doc, caseStudyId, navigate, onSave, onCreated]
  );

  const inspector = (
    <>
      <InspectorSection title="Status">
        <Segmented<EditorStatus>
          value={doc.status}
          onChange={(v) => set('status', v)}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'published', label: 'Published' },
          ]}
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
        />
        {doc.publishedAt ? (
          <p className="text-[11.5px] text-[color:var(--oa-muted)]">
            First published {new Date(doc.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        ) : null}
      </InspectorSection>

      <InspectorSection title="Case study details">
        <Field label="URL" hint={slugTouched ? undefined : 'Follows the title until you edit it.'}>
          <TextInput
            prefix="/case-study/"
            value={doc.slug}
            onChange={(e) => {
              setSlugTouched(true);
              set('slug', slugify(e.target.value));
            }}
            placeholder="case-study-url"
          />
        </Field>
        <Field label="Tags">
          <TagInput value={doc.tags} onChange={(v) => set('tags', v)} />
        </Field>
        <Field label="Deliverables">
          <TagInput value={doc.deliverables} onChange={(v) => set('deliverables', v)} placeholder="e.g. Fascia sign" />
        </Field>
      </InspectorSection>

      <InspectorSection title="Featured image">
        <ImageField value={doc.featuredImage || ''} onChange={(v) => set('featuredImage', v)} />
        <Field label="Fallback icon" hint="Lucide icon name shown when there is no image, e.g. Building2.">
          <TextInput value={doc.iconName || ''} onChange={(e) => set('iconName', e.target.value)} placeholder="Building2" />
        </Field>
      </InspectorSection>
    </>
  );

  return (
    <EditorShell
      section="Blog"
      kind="Case study"
      title={doc.title}
      status={doc.status}
      dirty={dirty}
      saving={saving}
      loading={loading}
      previewHref={caseStudyId && doc.slug ? `/case-study/${doc.slug}` : undefined}
      onBack={leave}
      onSaveDraft={() => save(false)}
      onPublish={() => save(true)}
      inspector={inspector}
    >
      <CanvasTitle
        eyebrow={doc.clientName || 'Case study'}
        title={doc.title}
        onTitle={(title) => setDoc((d) => ({ ...d, title, slug: slugTouched ? d.slug : slugify(title) }))}
        titlePlaceholder="Case study title"
        sub={
          <AutoTextarea
            bare
            minRows={2}
            value={doc.subtitle}
            onChange={(e) => set('subtitle', e.target.value)}
            placeholder="Subtitle: one or two sentences shown under the title."
            className="mt-3 text-[16px] leading-7 text-[color:var(--oa-muted)]"
          />
        }
      />

      <ClientStrip
        clientName={doc.clientName}
        onClientName={(v) => set('clientName', v)}
        clientLocation={doc.clientLocation}
        onClientLocation={(v) => set('clientLocation', v)}
        industry={doc.industry}
        onIndustry={(v) => set('industry', v)}
      />

      <CanvasSection title="Headline numbers">
        <StatsSection stats={doc.stats} onChange={(stats) => set('stats', stats)} />
      </CanvasSection>

      <CanvasSection title="The challenge">
        <AutoTextarea
          bare
          minRows={4}
          value={doc.challenge}
          onChange={(e) => set('challenge', e.target.value)}
          placeholder="What was the client trying to solve?"
          className="text-[15px] leading-7 text-[color:var(--oa-text)]"
        />
      </CanvasSection>

      <CanvasSection title="Our solution">
        <AutoTextarea
          bare
          minRows={4}
          value={doc.solution}
          onChange={(e) => set('solution', e.target.value)}
          placeholder="What did we deliver?"
          className="text-[15px] leading-7 text-[color:var(--oa-text)]"
        />
      </CanvasSection>

      <CanvasSection title="How we did it">
        <ProcessStepsSection steps={doc.processSteps} onChange={(steps) => set('processSteps', steps)} />
      </CanvasSection>

      <CanvasSection title="Results">
        <ResultsSection results={doc.results} onChange={(results) => set('results', results)} />
      </CanvasSection>

      <CanvasSection title="Gallery">
        <GallerySection items={doc.gallery} onChange={(gallery) => set('gallery', gallery)} />
      </CanvasSection>

      <CanvasSection title="Testimonial">
        <div className="border-l-[3px] border-[color:var(--oa-accent)] pl-4">
          <AutoTextarea
            bare
            minRows={2}
            value={doc.testimonialText || ''}
            onChange={(e) => set('testimonialText', e.target.value)}
            placeholder="Client quote"
            className="font-montserrat text-[17px] font-semibold italic leading-relaxed text-[color:var(--oa-text)]"
          />
          <div className="mt-2 flex flex-wrap gap-2 text-[13px]">
            <input
              value={doc.testimonialAuthor || ''}
              onChange={(e) => set('testimonialAuthor', e.target.value)}
              placeholder="Who said it"
              className="w-auto flex-1 bg-transparent font-medium text-[color:var(--oa-text)] outline-none placeholder:text-[color:var(--oa-faint)]"
            />
            <input
              value={doc.testimonialRole || ''}
              onChange={(e) => set('testimonialRole', e.target.value)}
              placeholder="Their role"
              className="w-auto flex-1 bg-transparent text-[color:var(--oa-muted)] outline-none placeholder:text-[color:var(--oa-faint)]"
            />
          </div>
        </div>
      </CanvasSection>
    </EditorShell>
  );
};

export default CaseStudyEditor;
